import { useCallback, useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import {
  buildBookingSpecializationOptions,
  getServicesForSpecialization,
  getBranchesForService as resolveBranchesForService,
} from "../../../utils/serviceCatalog";
import { filterDoctorsForBooking } from "../../../utils/doctorCatalog";
import s from "../clinic.module.css";
import {
  Button,
  Field,
  Input,
  Modal,
  Skeleton,
  useToast,
} from "../../ui";

const STEP_DEFS = [
  { id: "patient", label: "Клиент" },
  { id: "spec", label: "Специализация" },
  { id: "service", label: "Услуга" },
  { id: "branch", label: "Зал" },
  { id: "doctor", label: "Тренер" },
  { id: "time", label: "Время" },
];

const STEP_HINTS = {
  patient: "Выберите клиента для записи",
  spec: "Выберите направление — покажем подходящие услуги",
  service: "Выберите услугу",
  branch: "Выберите зал",
  doctor: "Выберите тренера",
  time: "Выберите дату и свободное время",
};

function slotIso(slot) {
  return typeof slot === "string" ? slot : slot?.start_datetime;
}

function resetFrom(level, prev, locks = {}) {
  const next = { ...prev };
  if (level <= 1) next.specializationId = locks.specializationId || "";
  if (level <= 2) next.serviceId = "";
  if (level <= 3) next.branchId = locks.branchId || "";
  if (level <= 4) next.doctorId = locks.doctorId || "";
  if (level <= 5) {
    next.date = locks.initialDate || toDateInputValue();
    next.slotIso = "";
  }
  return next;
}

function getBranchesForServiceList(
  serviceId,
  branches,
  scopedDoctors,
  fixedBranchId,
  bookableServices,
  specializationId = null,
) {
  if (!serviceId) return [];
  let list = resolveBranchesForService(
    serviceId,
    branches,
    bookableServices,
    scopedDoctors,
    specializationId,
  );
  if (fixedBranchId) list = list.filter((b) => b.id === fixedBranchId);
  return list;
}

function getDoctorsForServiceBranch(
  serviceId,
  branchId,
  scopedDoctors,
  specializationId = null,
) {
  return filterDoctorsForBooking(scopedDoctors, {
    serviceId,
    branchId,
    specializationId,
  });
}

function mergeFormState(prev, patch, resetLevel, locks, fixedBranchId, fixedDoctorId) {
  let merged = { ...prev, ...patch };
  if (resetLevel != null) merged = resetFrom(resetLevel, merged, locks);
  if (fixedBranchId && (patch.serviceId != null || merged.serviceId)) {
    merged.branchId = fixedBranchId;
  }
  if (
    fixedDoctorId &&
    (patch.serviceId != null ||
      patch.branchId != null ||
      merged.serviceId ||
      merged.branchId)
  ) {
    merged.doctorId = fixedDoctorId;
  }
  return merged;
}

function resolveNextStepIndex(fromIndex, merged, steps, locks) {
  let next = fromIndex + 1;
  while (next < steps.length) {
    const id = steps[next].id;
    if (id === "branch") {
      if (locks.branchId || merged.branchId) {
        if (locks.branchId) merged.branchId = locks.branchId;
        next += 1;
        continue;
      }
    }
    if (id === "doctor") {
      if (locks.doctorId || merged.doctorId) {
        if (locks.doctorId) merged.doctorId = locks.doctorId;
        next += 1;
        continue;
      }
    }
    break;
  }
  return Math.min(next, steps.length - 1);
}

async function fetchBookingCatalog() {
  try {
    return await clinicAPI.getBookingCatalog();
  } catch {
    const [catalog, branchesRes] = await Promise.all([
      clinicAPI.getServicesCatalog(),
      clinicAPI.getBranches(),
    ]);
    const doctorsRes = await clinicAPI.getDoctors({
      active: "true",
      include_summary: "false",
    });
    const doctors = (doctorsRes.doctors || []).map((d) => ({
      ...d,
      branch_ids: (d.branches || []).map((b) => b.id),
      service_ids: [],
    }));
    const byDoctor = {};
    for (const sv of catalog.services || []) {
      for (const doctorId of sv.doctor_ids || []) {
        if (!byDoctor[doctorId]) byDoctor[doctorId] = [];
        if (!byDoctor[doctorId].includes(sv.id)) byDoctor[doctorId].push(sv.id);
      }
    }
    return {
      specializations: catalog.specializations || [],
      services: catalog.services || [],
      branches: branchesRes.branches || [],
      doctors: doctors.map((d) => ({
        ...d,
        service_ids: byDoctor[d.id] || [],
      })),
    };
  }
}

export default function AppointmentBookWizard({
  open,
  onClose,
  onBooked,
  title = "Новая запись",
  patientId: fixedPatientId,
  patientName: fixedPatientName,
  doctorId: fixedDoctorId,
  branchId: fixedBranchId,
  specializationId: fixedSpecializationId,
  initialDate,
  isBlocked = false,
  onError,
}) {
  const toast = useToast();
  const notifyError = useCallback(
    (msg) => {
      if (onError) onError(msg);
      else toast.error(msg);
    },
    [onError, toast],
  );

  const locks = useMemo(
    () => ({
      patientId: fixedPatientId || "",
      doctorId: fixedDoctorId || "",
      branchId: fixedBranchId || "",
      specializationId: fixedSpecializationId || "",
      initialDate: initialDate || toDateInputValue(),
    }),
    [
      fixedPatientId,
      fixedDoctorId,
      fixedBranchId,
      fixedSpecializationId,
      initialDate,
    ],
  );

  const skipSpecStep = Boolean(locks.doctorId || locks.specializationId);

  const steps = useMemo(
    () =>
      STEP_DEFS.filter((st) => {
        if (st.id === "patient" && locks.patientId) return false;
        if (st.id === "spec" && skipSpecStep) return false;
        if (st.id === "doctor" && locks.doctorId) return false;
        if (st.id === "branch" && locks.branchId) return false;
        return true;
      }),
    [locks, skipSpecStep],
  );

  const [step, setStep] = useState(0);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);

  const [patients, setPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");
  const [services, setServices] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsByDate, setSlotsByDate] = useState({});
  /** date (YYYY-MM-DD) → 'loading' | 'available' | 'empty' */
  const [dateAvailability, setDateAvailability] = useState({});
  const [loadingDateAvailability, setLoadingDateAvailability] = useState(false);

  const [form, setForm] = useState({
    patientId: "",
    specializationId: "",
    serviceId: "",
    branchId: "",
    doctorId: "",
    date: toDateInputValue(),
    slotIso: "",
  });

  const currentStepId = steps[step]?.id;

  const bookDates = useMemo(() => {
    if (initialDate) {
      return [
        {
          value: initialDate,
          day: new Date(`${initialDate}T12:00:00`).toLocaleDateString("ru-RU", {
            weekday: "short",
          }),
          num: new Date(`${initialDate}T12:00:00`).getDate(),
        },
      ];
    }
    const list = [];
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    for (let i = 0; i < 21; i++) {
      const x = new Date(d);
      x.setDate(d.getDate() + i);
      list.push({
        value: toDateInputValue(x),
        day: x.toLocaleDateString("ru-RU", { weekday: "short" }),
        num: x.getDate(),
      });
    }
    return list;
  }, [initialDate]);

  const scopedDoctors = useMemo(() => {
    if (!fixedDoctorId) return doctors;
    return doctors.filter((d) => d.id === fixedDoctorId);
  }, [doctors, fixedDoctorId]);

  const bookableServices = useMemo(() => {
    let list = services.filter((sv) => (sv.doctor_ids || []).length > 0);
    if (fixedDoctorId) {
      list = list.filter((sv) => (sv.doctor_ids || []).includes(fixedDoctorId));
    }
    return list;
  }, [services, fixedDoctorId]);

  const specializationOptions = useMemo(
    () => buildBookingSpecializationOptions(specializations, bookableServices),
    [specializations, bookableServices],
  );

  const servicesForSpec = useMemo(() => {
    if (locks.doctorId) {
      return bookableServices;
    }
    const specId = locks.specializationId || form.specializationId;
    if (!specId) return [];
    return getServicesForSpecialization(bookableServices, specId);
  }, [
    bookableServices,
    form.specializationId,
    locks.doctorId,
    locks.specializationId,
  ]);

  const effectiveSpecializationId =
    locks.specializationId || form.specializationId;

  const branchesForService = useMemo(
    () =>
      getBranchesForServiceList(
        form.serviceId,
        branches,
        scopedDoctors,
        fixedBranchId,
        bookableServices,
        effectiveSpecializationId,
      ),
    [
      form.serviceId,
      branches,
      scopedDoctors,
      fixedBranchId,
      bookableServices,
      effectiveSpecializationId,
    ],
  );

  const doctorsAvailable = useMemo(
    () =>
      getDoctorsForServiceBranch(
        form.serviceId,
        form.branchId,
        scopedDoctors,
        form.specializationId,
      ),
    [form.serviceId, form.branchId, scopedDoctors, form.specializationId],
  );

  const filteredPatients = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return patients.slice(0, 80);
    return patients
      .filter((p) => {
        const hay = [p.full_name, p.email, p.phone]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [patients, patientQuery]);

  const effectivePatientId = form.patientId || locks.patientId;
  const effectiveDoctorId = form.doctorId || locks.doctorId;
  const effectiveBranchId = form.branchId || locks.branchId;

  const slotCacheKey = `${effectiveDoctorId}-${effectiveBranchId}-${form.serviceId}`;

  const selectedPatient =
    patients.find((p) => p.id === effectivePatientId) ||
    (fixedPatientName ? { full_name: fixedPatientName } : null);
  const selectedService = services.find((sv) => sv.id === form.serviceId);
  const selectedBranch = branches.find((b) => b.id === effectiveBranchId);
  const selectedDoctor =
    scopedDoctors.find((d) => d.id === effectiveDoctorId) ||
    doctors.find((d) => d.id === effectiveDoctorId);
  const effectiveSpecId = locks.specializationId || form.specializationId;
  const selectedSpec =
    specializationOptions.find((sp) => sp.id === effectiveSpecId) ||
    specializations.find((sp) => sp.id === effectiveSpecId);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPatientQuery("");
    setForm({
      patientId: locks.patientId,
      specializationId: locks.specializationId,
      serviceId: "",
      branchId: locks.branchId,
      doctorId: locks.doctorId,
      date: locks.initialDate,
      slotIso: "",
    });
    setSlots([]);
    setSlotsByDate({});
    setDateAvailability({});
    setLoadingCatalog(true);

    const loads = [fetchBookingCatalog()];
    if (!locks.patientId) {
      loads.unshift(clinicAPI.getPatients({ limit: 500 }));
    }

    Promise.all(loads)
      .then((results) => {
        const catalog = locks.patientId ? results[0] : results[1];
        if (!locks.patientId) {
          setPatients(results[0].patients || []);
        }
        setServices(catalog.services || []);
        setSpecializations(catalog.specializations || []);
        setBranches(catalog.branches || []);
        setDoctors(catalog.doctors || []);
      })
      .catch(() => notifyError("Не удалось загрузить данные для записи"))
      .finally(() => setLoadingCatalog(false));
  }, [
    open,
    locks.patientId,
    locks.branchId,
    locks.doctorId,
    locks.specializationId,
    locks.initialDate,
    notifyError,
  ]);

  useEffect(() => {
    if (!open) return;
    setSlotsByDate({});
    setDateAvailability({});
    setSlots([]);
  }, [open, slotCacheKey]);

  useEffect(() => {
    if (
      !open ||
      currentStepId !== "time" ||
      !effectiveDoctorId ||
      !effectiveBranchId ||
      !form.serviceId
    ) {
      return;
    }

    const dateList = bookDates.map((d) => d.value);
    if (dateList.length === 0) return;

    const from = dateList[0];
    const to = dateList[dateList.length - 1];

    let cancelled = false;
    setLoadingDateAvailability(true);
    setDateAvailability((prev) => ({
      ...prev,
      ...Object.fromEntries(
        dateList.map((d) => [d, prev[d] === "available" || prev[d] === "empty" ? prev[d] : "loading"]),
      ),
    }));

    clinicAPI
      .getSlotsRange(
        effectiveDoctorId,
        effectiveBranchId,
        from,
        to,
        form.serviceId,
      )
      .then((r) => {
        if (cancelled) return;
        const byDate = r.slots_by_date || {};
        const availPatch = {};
        const slotsPatch = {};
        for (const date of dateList) {
          const daySlots = byDate[date] || [];
          availPatch[date] = daySlots.length > 0 ? "available" : "empty";
          slotsPatch[date] = daySlots;
        }
        setDateAvailability((prev) => ({ ...prev, ...availPatch }));
        setSlotsByDate((prev) => ({ ...prev, ...slotsPatch }));
        setForm((prevForm) => {
          if (availPatch[prevForm.date] === "available") return prevForm;
          const firstAvailable = dateList.find(
            (d) => availPatch[d] === "available",
          );
          if (firstAvailable && firstAvailable !== prevForm.date) {
            return { ...prevForm, date: firstAvailable, slotIso: "" };
          }
          return prevForm;
        });
      })
      .catch(() => {
        if (cancelled) return;
        const emptyPatch = Object.fromEntries(
          dateList.map((d) => [d, "empty"]),
        );
        setDateAvailability((prev) => ({ ...prev, ...emptyPatch }));
        setSlotsByDate((prev) => ({
          ...prev,
          ...Object.fromEntries(dateList.map((d) => [d, []])),
        }));
      })
      .finally(() => {
        if (!cancelled) setLoadingDateAvailability(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    currentStepId,
    effectiveDoctorId,
    effectiveBranchId,
    form.serviceId,
    bookDates,
    slotCacheKey,
  ]);

  useEffect(() => {
    if (loadingDateAvailability || !form.date) return;
    if (!(form.date in slotsByDate)) return;
    setSlots(slotsByDate[form.date] || []);
  }, [form.date, slotsByDate, loadingDateAvailability]);

  const patchForm = (patch, resetLevel) => {
    setForm((prev) => {
      let merged = { ...prev, ...patch };
      if (resetLevel != null) merged = resetFrom(resetLevel, merged, locks);
      if (fixedBranchId && patch.serviceId) merged.branchId = fixedBranchId;
      if (fixedDoctorId && (patch.serviceId || patch.branchId)) {
        merged.doctorId = fixedDoctorId;
      }
      return merged;
    });
  };

  /** Выбор значения и переход на следующий шаг (кроме последнего — время) */
  const selectAndAdvance = (patch, resetLevel) => {
    const merged = mergeFormState(
      form,
      patch,
      resetLevel,
      locks,
      fixedBranchId,
      fixedDoctorId,
    );
    const stepId = currentStepId;

    if (stepId === "service" && patch.serviceId) {
      const availBranches = getBranchesForServiceList(
        merged.serviceId,
        branches,
        scopedDoctors,
        fixedBranchId,
        bookableServices,
        locks.specializationId || merged.specializationId,
      );
      if (availBranches.length === 0) {
        notifyError("Нет залов, где оказывают эту услугу");
        setForm(merged);
        return;
      }
      if (availBranches.length === 1 && !fixedBranchId) {
        merged.branchId = availBranches[0].id;
      }
    }

    if (stepId === "branch" && patch.branchId) {
      const availDoctors = getDoctorsForServiceBranch(
        merged.serviceId,
        merged.branchId,
        scopedDoctors,
        merged.specializationId,
      );
      if (availDoctors.length === 0) {
        notifyError("Нет тренеров для этой услуги в выбранном зале");
        setForm(merged);
        return;
      }
      if (availDoctors.length === 1 && !fixedDoctorId) {
        merged.doctorId = availDoctors[0].id;
      }
    }

    setForm(merged);

    if (stepId !== "time" && step < steps.length - 1) {
      setStep(resolveNextStepIndex(step, merged, steps, locks));
    }
  };

  const goToStep = (targetIndex) => {
    if (targetIndex >= step) return;
    setStep(targetIndex);
  };

  const validateStep = () => {
    if (currentStepId === "patient" && !form.patientId) {
      notifyError("Выберите клиента");
      return false;
    }
    if (currentStepId === "spec" && !skipSpecStep && !form.specializationId) {
      notifyError("Выберите специализацию");
      return false;
    }
    if (currentStepId === "service" && !form.serviceId) {
      notifyError("Выберите услугу");
      return false;
    }
    if (currentStepId === "branch" && !effectiveBranchId) {
      notifyError("Выберите зал");
      return false;
    }
    if (currentStepId === "doctor" && !effectiveDoctorId) {
      notifyError("Выберите тренера");
      return false;
    }
    if (currentStepId === "time" && !form.slotIso) {
      notifyError("Выберите время");
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => setStep((i) => Math.max(i - 1, 0));

  const submit = async () => {
    if (isBlocked) {
      notifyError("Клиент заблокирован");
      return;
    }
    if (!validateStep()) return;
    setSaving(true);
    try {
      await clinicAPI.createAppointment({
        patient_id: effectivePatientId,
        doctor_id: effectiveDoctorId,
        branch_id: effectiveBranchId,
        service_id: form.serviceId,
        start_datetime: form.slotIso,
      });
      toast.success("Запись создана");
      onBooked?.();
      onClose?.();
    } catch (e) {
      notifyError(e.response?.data?.error || "Не удалось записать");
    } finally {
      setSaving(false);
    }
  };

  const progressParts = [
    locks.patientId && selectedPatient
      ? selectedPatient.full_name || selectedPatient.email
      : null,
    !skipSpecStep ? selectedSpec?.name : null,
    selectedService?.name,
    !locks.branchId ? selectedBranch?.name : null,
    !locks.doctorId ? selectedDoctor?.full_name : null,
    form.slotIso ? formatTimeRu(form.slotIso) : null,
  ].filter(Boolean);

  const footer = (
    <div className={s.bookWizardFooter}>
      <Button type="button" variant="secondary" onClick={onClose}>
        Отмена
      </Button>
      {step > 0 ? (
        <Button type="button" variant="secondary" onClick={goBack}>
          Назад
        </Button>
      ) : null}
      {step < steps.length - 1 ? (
        <Button type="button" onClick={goNext}>
          Далее
        </Button>
      ) : (
        <Button type="button" onClick={submit} disabled={saving || isBlocked}>
          {saving ? "Запись…" : "Записать"}
        </Button>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} size="xl" footer={footer}>
      <div className={s.bookWizard}>
        <div className={s.bookWizardSteps}>
          {steps.map((st, i) => {
            const isActive = i === step;
            const isDone = i < step;
            const className = `${s.bookWizardStep} ${
              isActive
                ? s.bookWizardStepActive
                : isDone
                  ? s.bookWizardStepDone
                  : ""
            }`;
            if (isDone) {
              return (
                <button
                  key={st.id}
                  type="button"
                  className={className}
                  onClick={() => goToStep(i)}
                >
                  {st.label}
                </button>
              );
            }
            return (
              <div key={st.id} className={className}>
                {st.label}
              </div>
            );
          })}
        </div>

        {(locks.patientId ||
          locks.doctorId ||
          locks.branchId ||
          locks.specializationId ||
          initialDate) && (
          <div className={s.bookWizardMeta}>
            {locks.patientId && selectedPatient ? (
              <span className={s.bookWizardBadge}>
                {selectedPatient.full_name || selectedPatient.email}
              </span>
            ) : null}
            {skipSpecStep && selectedSpec?.name ? (
              <span className={s.bookWizardBadge}>{selectedSpec.name}</span>
            ) : null}
            {locks.doctorId && selectedDoctor ? (
              <span className={s.bookWizardBadge}>{selectedDoctor.full_name}</span>
            ) : null}
            {locks.branchId && selectedBranch ? (
              <span className={s.bookWizardBadge}>{selectedBranch.name}</span>
            ) : null}
            {initialDate ? (
              <span className={s.bookWizardBadge}>
                {formatDateRu(locks.initialDate)}
              </span>
            ) : null}
          </div>
        )}

        {progressParts.length > 0 && step > 0 ? (
          <p className={s.bookWizardSummary}>{progressParts.join(" → ")}</p>
        ) : null}

        <div className={s.bookWizardBody}>
          {loadingCatalog ? (
            <Skeleton height={160} />
          ) : (
            <>
              <h3 className={s.bookWizardStepTitle}>
                {currentStepId === "service" && locks.doctorId
                  ? "Выберите услугу тренера"
                  : STEP_HINTS[currentStepId] || ""}
              </h3>

              <div key={currentStepId}>
              {currentStepId === "patient" && (
                <>
                  <Field label="Поиск">
                    <Input
                      value={patientQuery}
                      onChange={(e) => setPatientQuery(e.target.value)}
                      placeholder="ФИО, email или телефон"
                    />
                  </Field>
                  <div className={s.bookWizardList} style={{ marginTop: 12 }}>
                    {filteredPatients.length === 0 ? (
                      <p className={s.docEmptyHint}>Клиенты не найдены</p>
                    ) : (
                      filteredPatients.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={
                            form.patientId === p.id
                              ? s.bookWizardListItemSelected
                              : s.bookWizardListItem
                          }
                          onClick={() => selectAndAdvance({ patientId: p.id })}
                        >
                          <span>
                            <div className={s.bookWizardListMain}>
                              {p.full_name || p.email}
                            </div>
                            {p.phone ? (
                              <div className={s.bookWizardListMeta}>
                                {p.phone}
                              </div>
                            ) : null}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}

              {currentStepId === "spec" && (
                <div className={s.bookWizardSpecGrid}>
                  {specializationOptions.length === 0 ? (
                    <p className={s.docEmptyHint}>
                      Нет услуг с привязанными тренерами. Добавьте услуги в разделе
                      «Услуги» и назначьте их тренерам.
                    </p>
                  ) : (
                    specializationOptions.map((sp) => (
                      <button
                        key={sp.id}
                        type="button"
                        className={
                          form.specializationId === sp.id
                            ? s.bookWizardSpecTileSelected
                            : s.bookWizardSpecTile
                        }
                        onClick={() =>
                          selectAndAdvance({ specializationId: sp.id }, 2)
                        }
                      >
                        <span className={s.bookWizardSpecName}>{sp.name}</span>
                        <span className={s.bookWizardSpecCount}>
                          {sp.count}{" "}
                          {sp.count === 1 ? "услуга" : sp.count < 5 ? "услуги" : "услуг"}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {currentStepId === "service" && (
                <div className={s.bookWizardList}>
                  {servicesForSpec.length === 0 ? (
                    <p className={s.docEmptyHint}>
                      {locks.doctorId
                        ? "У тренера нет назначенных услуг — привяжите во вкладке «Услуги»"
                        : "Нет услуг в этой специализации"}
                    </p>
                  ) : (
                    servicesForSpec.map((sv) => (
                      <button
                        key={sv.id}
                        type="button"
                        className={
                          form.serviceId === sv.id
                            ? s.bookWizardListItemSelected
                            : s.bookWizardListItem
                        }
                        onClick={() => selectAndAdvance({ serviceId: sv.id }, 3)}
                      >
                        <span>
                          <div className={s.bookWizardListMain}>{sv.name}</div>
                          {sv.duration_minutes ? (
                            <div className={s.bookWizardListMeta}>
                              {sv.duration_minutes} мин
                            </div>
                          ) : null}
                        </span>
                        {sv.price != null ? (
                          <span className={s.bookWizardPrice}>{sv.price} ₽</span>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              )}

              {currentStepId === "branch" && (
                <div className={s.bookWizardList}>
                  {branchesForService.length === 0 ? (
                    <p className={s.docEmptyHint}>
                      Нет залов, где тренеры ведут эту услугу
                    </p>
                  ) : (
                    branchesForService.map((br) => (
                      <button
                        key={br.id}
                        type="button"
                        className={
                          form.branchId === br.id
                            ? `${s.bookWizardListItem} ${s.bookWizardListItemSelected}`
                            : s.bookWizardListItem
                        }
                        onClick={() => selectAndAdvance({ branchId: br.id }, 4)}
                      >
                        <span>
                          <div className={s.bookWizardListMain}>{br.name}</div>
                          {br.address || br.metro ? (
                            <div className={s.bookWizardBranchMeta}>
                              {[br.address, br.metro].filter(Boolean).join(" · ")}
                            </div>
                          ) : null}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {currentStepId === "doctor" && (
                <div className={s.doctorGrid}>
                  {doctorsAvailable.length === 0 ? (
                    <p className={s.docEmptyHint}>
                      Нет тренеров для выбранной услуги в этом зале
                    </p>
                  ) : (
                    doctorsAvailable.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className={
                          form.doctorId === d.id
                            ? s.doctorCardSelected
                            : s.doctorCard
                        }
                        onClick={() => selectAndAdvance({ doctorId: d.id }, 5)}
                      >
                        <div className={s.doctorName}>{d.full_name}</div>
                        {d.specialization ? (
                          <div className={s.doctorSpec}>{d.specialization}</div>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              )}

              {currentStepId === "time" && (
                <>
                  {!initialDate ? (
                    <>
                      <div className={s.dateStrip}>
                        {bookDates.map((d) => {
                          const avail = dateAvailability[d.value];
                          return (
                            <button
                              key={d.value}
                              type="button"
                              className={[
                                s.dateChip,
                                form.date === d.value ? s.dateChipSelected : "",
                                avail === "empty" ? s.dateChipEmpty : "",
                                avail === "available" ? s.dateChipHasSlots : "",
                                avail === "loading" ? s.dateChipLoading : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              disabled={avail === "empty" || avail === "loading"}
                              title={
                                avail === "empty"
                                  ? "Нет свободных слотов"
                                  : avail === "available"
                                    ? "Есть свободные слоты"
                                    : undefined
                              }
                              onClick={() =>
                                patchForm({ date: d.value, slotIso: "" })
                              }
                            >
                              <div className={s.dateChipDay}>{d.day}</div>
                              <div className={s.dateChipNum}>{d.num}</div>
                            </button>
                          );
                        })}
                      </div>
                      {!loadingDateAvailability ? (
                        <p className={s.dateStripLegend}>
                          Серым отмечены дни без свободных слотов
                        </p>
                      ) : null}
                    </>
                  ) : null}
                  {loadingDateAvailability && !(form.date in slotsByDate) ? (
                    <Skeleton height={80} />
                  ) : slots.length > 0 ? (
                    <div className={s.timeGrid}>
                      {slots.map((sl) => {
                        const iso = slotIso(sl);
                        const selected = form.slotIso === iso;
                        return (
                          <button
                            key={iso}
                            type="button"
                            className={`${s.timeSlot} ${
                              selected ? s.timeSlotSelected : ""
                            }`}
                            onClick={() => patchForm({ slotIso: iso })}
                          >
                            {formatTimeRu(iso)}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={s.docEmptyHint}>
                      {dateAvailability[form.date] === "empty"
                        ? `На ${formatDateRu(form.date)} нет свободных слотов. Выберите другой день.`
                        : `Нет свободных слотов на ${formatDateRu(form.date)}. Выберите другую дату или проверьте расписание тренера.`}
                    </p>
                  )}
                </>
              )}

              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
